/**
 * SlidePreviewModal
 *
 * A full-screen dialog that shows a large preview image of the slide along
 * with its AI-generated metadata (title, summary, keywords, category, and
 * source file).  Includes an "Add to builder" action button.
 *
 * Admins can toggle into Edit mode to inline-edit title, summary, keywords,
 * and category (#2). Saves via PUT /api/slide-sense/admin/slides/<id>.
 */

import { useCallback, useEffect, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SlideSenseSlide } from "@/features/slide-sense/types"

import {
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { updateSlideMetadata } from "@/features/slide-sense/admin-api"
import { cn } from "@/lib/utils"

import { SlideCategoryBadge } from "./SlideCategoryBadge"
import { type SlideMetadataDraft, SlideMetadataEditor } from "./SlideMetadataEditor"
import { resolveSlideImageUrl } from "../hooks/use-slide-image"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SlidePreviewModalProps {
  slide: SlideSenseSlide | null
  open: boolean
  onClose: () => void
  onAddToBuilder?: (slide: SlideSenseSlide) => void
  /** All slides currently visible in the library (for prev/next navigation). */
  slides?: SlideSenseSlide[]
  /** Called when user navigates to a different slide. */
  onNavigate?: (slide: SlideSenseSlide) => void
  /** When true, the modal exposes an Edit metadata button. */
  canEdit?: boolean
}

function draftFromSlide(slide: SlideSenseSlide): SlideMetadataDraft {
  return {
    ai_title: slide.ai_title,
    ai_summary: slide.ai_summary,
    ai_keywords: [...slide.ai_keywords],
    category: slide.category,
  }
}

function draftsEqual(a: SlideMetadataDraft, b: SlideMetadataDraft): boolean {
  if (a.ai_title !== b.ai_title) return false
  if (a.ai_summary !== b.ai_summary) return false
  if (a.category !== b.category) return false
  if (a.ai_keywords.length !== b.ai_keywords.length) return false
  for (let i = 0; i < a.ai_keywords.length; i++) {
    if (a.ai_keywords[i] !== b.ai_keywords[i]) return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SlidePreviewModal({
  slide,
  open,
  onClose,
  onAddToBuilder,
  slides = [],
  onNavigate,
  canEdit = false,
}: SlidePreviewModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [imgError, setImgError] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState<SlideMetadataDraft | null>(null)
  const [newKeyword, setNewKeyword] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  const fetchPreview = useCallback((slideId: string) => {
    setImgError(false)
    setImageUrl(resolveSlideImageUrl(slideId))
  }, [])

  useEffect(() => {
    if (slide?.id) {
      fetchPreview(slide.id)
    } else {
      setImgError(false)
      setImageUrl(null)
    }
  }, [slide?.id, fetchPreview])

  // Exit edit mode whenever the open slide changes or the modal closes.
  useEffect(() => {
    setIsEditing(false)
    setDraft(null)
    setNewKeyword("")
  }, [slide?.id, open])

  // Navigation
  const currentIndex = slide ? slides.findIndex((s) => s.id === slide.id) : -1
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < slides.length - 1

  const goPrev = useCallback(() => {
    if (hasPrev && onNavigate) onNavigate(slides[currentIndex - 1])
  }, [hasPrev, onNavigate, slides, currentIndex])

  const goNext = useCallback(() => {
    if (hasNext && onNavigate) onNavigate(slides[currentIndex + 1])
  }, [hasNext, onNavigate, slides, currentIndex])

  // Keyboard navigation (left/right arrows) — disabled while editing so
  // arrow keys inside inputs don't navigate slides.
  useEffect(() => {
    if (!open || isEditing) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev()
      else if (e.key === "ArrowRight") goNext()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, isEditing, goPrev, goNext])

  if (!slide) return null

  const hasImage = Boolean(imageUrl && !imgError)
  const showError = !hasImage && imgError

  const startEdit = () => {
    setDraft(draftFromSlide(slide))
    setNewKeyword("")
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setDraft(null)
    setNewKeyword("")
    setIsEditing(false)
  }

  const addKeywordFromInput = () => {
    if (!draft) return
    const trimmed = newKeyword.trim().slice(0, 60)
    if (!trimmed) return
    if (draft.ai_keywords.some((k) => k.toLowerCase() === trimmed.toLowerCase())) {
      setNewKeyword("")
      return
    }
    if (draft.ai_keywords.length >= 30) {
      setNewKeyword("")
      return
    }
    setDraft({ ...draft, ai_keywords: [...draft.ai_keywords, trimmed] })
    setNewKeyword("")
  }

  const removeKeyword = (kw: string) => {
    if (!draft) return
    setDraft({ ...draft, ai_keywords: draft.ai_keywords.filter((k) => k !== kw) })
  }

  const handleSave = async () => {
    if (!draft) return
    const original = draftFromSlide(slide)
    if (draftsEqual(draft, original)) {
      cancelEdit()
      return
    }

    setIsSaving(true)
    try {
      const updated = await updateSlideMetadata(slide.id, {
        ai_title: draft.ai_title.trim().slice(0, 300),
        ai_summary: draft.ai_summary.trim().slice(0, 2000),
        ai_keywords: draft.ai_keywords,
        category: draft.category,
      })
      toast.success(t("slideSense.metadataSaved"))
      await queryClient.invalidateQueries({ queryKey: ["slide-sense"] })
      // The modal's slide is owned by the parent (via `previewSlide` state).
      // Query invalidation alone doesn't update the prop, so the open modal
      // would keep showing the stale title/summary/keywords until close+reopen.
      // Reuse onNavigate to push the freshly-saved record back to the parent
      // — same callback the prev/next arrows use, just with the updated copy.
      if (onNavigate) onNavigate(updated)
      setIsEditing(false)
      setDraft(null)
    } catch (err) {
      console.error("Slide metadata update failed:", err)
      toast.error(t("slideSense.metadataSaveFailed"))
    } finally {
      setIsSaving(false)
    }
  }

  const renderPreview = () => {
    if (hasImage && imageUrl) {
      return (
        <img
          alt={slide.ai_title}
          className="h-full w-full object-contain"
          src={imageUrl}
          onError={() => setImgError(true)}
        />
      )
    }
    if (showError) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center">
          <ImageOff className="text-muted-foreground/60 h-10 w-10" />
          <p className="text-muted-foreground text-sm">{t("slideSense.previewLoadFailed")}</p>
          <Button
            className="gap-1.5"
            size="sm"
            variant="outline"
            onClick={() => fetchPreview(slide.id)}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("slideSense.retry")}
          </Button>
        </div>
      )
    }
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ImageOff className="text-muted-foreground/40 h-12 w-12" />
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="flex-1">{slide.ai_title}</DialogTitle>
            {canEdit && !isEditing && (
              <Button
                aria-label={t("slideSense.editMetadata")}
                className="gap-1.5"
                size="sm"
                variant="outline"
                onClick={startEdit}
              >
                <Pencil className="h-3.5 w-3.5" />
                {t("slideSense.edit")}
              </Button>
            )}
          </div>
          <DialogDescription className="sr-only">
            {t("slideSense.previewDescription")}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <div
              className={cn(
                "bg-muted/40 relative w-full overflow-hidden rounded-lg border",
                "aspect-[16/9]"
              )}
            >
              {renderPreview()}
            </div>

            <div className="space-y-3">
              {isEditing && draft ? (
                <SlideMetadataEditor
                  draft={draft}
                  newKeyword={newKeyword}
                  setDraft={setDraft}
                  setNewKeyword={setNewKeyword}
                  sourceFilename={slide.source_filename}
                  onAddKeyword={addKeywordFromInput}
                  onRemoveKeyword={removeKeyword}
                />
              ) : (
                <ReadOnlyMetadata slide={slide} />
              )}
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div className="flex items-center gap-1">
            {!isEditing && slides.length > 1 && (
              <>
                <Button
                  aria-label={t("slideSense.previousSlide")}
                  className="h-8 w-8"
                  disabled={!hasPrev}
                  size="icon"
                  variant="outline"
                  onClick={goPrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-muted-foreground min-w-[3rem] text-center text-xs tabular-nums">
                  {currentIndex + 1} / {slides.length}
                </span>
                <Button
                  aria-label={t("slideSense.nextSlide")}
                  className="h-8 w-8"
                  disabled={!hasNext}
                  size="icon"
                  variant="outline"
                  onClick={goNext}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button disabled={isSaving} size="sm" variant="outline" onClick={cancelEdit}>
                  {t("slideSense.cancel")}
                </Button>
                <Button
                  className="gap-1.5"
                  disabled={isSaving}
                  size="sm"
                  onClick={() => void handleSave()}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {t("slideSense.save")}
                </Button>
              </>
            ) : (
              onAddToBuilder && (
                <Button
                  className="gap-1.5"
                  data-testid="slide-preview-add"
                  onClick={() => {
                    // Keep the dialog open — the user flips to the next
                    // slide with the footer arrows and keeps adding
                    // (#2127). The toast is the add-confirmation.
                    onAddToBuilder(slide)
                    toast.success(t("slideSense.addedToBuilder"))
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {t("slideSense.addToBuilder")}
                </Button>
              )
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReadOnlyMetadata({ slide }: { slide: SlideSenseSlide }) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <SlideCategoryBadge category={slide.category} />
        <span className="text-muted-foreground text-xs">{slide.source_filename}</span>
      </div>

      {slide.ai_summary && (
        <div>
          <h4 className="text-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
            {t("slideSense.summary")}
          </h4>
          <p className="text-muted-foreground text-sm leading-relaxed">{slide.ai_summary}</p>
        </div>
      )}

      {slide.ai_keywords.length > 0 && (
        <div>
          <h4 className="text-foreground mb-1.5 text-xs font-semibold tracking-wide uppercase">
            {t("slideSense.keywords")}
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {slide.ai_keywords.map((kw) => (
              <Badge key={kw} className="text-[11px]" variant="secondary">
                {kw}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
